import React, { useEffect, useState } from "react";
import {
    FormGroup,
    Input,
    Modal,
    ModalBody,
    Label,
    Col
} from "reactstrap";
import Select from "react-select";
import "react-toastify/dist/ReactToastify.css";

import SubscriptionStatus from "./Section/SubscriptionStatus";
import PreventEnterSubmitForm from "../../components/Forms/PreventEnterSubmitForm";
import ActionModalHeader from "../../components/ActionModalHeader/ActionModalHeader";
import Spinner from "../../components/UISpinner/UISpinner";
import Button from "../../components/UIButton/UIButton"; // same as Location modal

const emptyFormData = {
    accountId: "",
    deviceType: "",
    name: "",
    uDID: "",
    serialNumber: "",
    lastUpdated: "",
    DeviceTypeName: "",
    description: "",
    seatDetails: "",
    location: "",
    printer: ""
};

const ManageDeviceModal = ({
    isLoading,
    isOpen,
    handleClose,
    submitHandler,
    manageData,
    locationData,
    printerData,
    modalMode,
    refreshDevices,
    toastCallback
}) => {
    const editDevice = modalMode === "create" ? false : true;
    const [formData, setFormData] = useState(emptyFormData);
    const [locationSelectedOption, setLocationSelectedOption] = useState(null);
    const [printerSelectedOption, setPrinterSelectedOption] = useState(null);
    const closeModalAndRefreshPage = () => {
        handleClose();
        refreshDevices();
    }
    const printers =
        printerData?.map((data) => ({ label: data.name, value: data.id })) ?? [];
    const previousPrinter = manageData?.printerId ?? -1;

    // hydrate form state from manageData whenever device changes / modal opens
    useEffect(() => {
        if (!isOpen) return;

        if (manageData) {
            const locationOption =
                locationData?.find(
                    ({ value }) => value === manageData?.locationId?.toString()
                ) ?? null;

            const printerOption =
                printers.find((x) => x.value === previousPrinter) ?? null;

            setFormData({
                ...emptyFormData,
                accountId: manageData.accountId ?? "",
                deviceType: manageData.deviceType ?? "",
                name: manageData.deviceName ?? "",
                uDID: manageData.udid ?? "",
                serialNumber: manageData.serialNumber ?? "",
                lastUpdated: manageData.lastUpdated
                    ? new Date(manageData.lastUpdated).toString()
                    : "",
                DeviceTypeName: manageData.deviceTypeName ?? "",
                description: manageData.description ?? "",
                location: locationOption?.value ?? "",
                printer: printerOption?.value ?? ""
            });

            setLocationSelectedOption(locationOption);
            setPrinterSelectedOption(printerOption);
        } else {
            setFormData(emptyFormData);
            setLocationSelectedOption(null);
            setPrinterSelectedOption(null);
        }
    }, [manageData, isOpen, locationData, previousPrinter]);


    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [id]: value
        }));
    };

    const handleLocationSelectChange = (location) => {
        setLocationSelectedOption(location);
        setFormData((prev) => ({
            ...prev,
            location: location?.value ?? ""
        }));
    };

    const handlePrinterSelectChange = (printer) => {
        setPrinterSelectedOption(printer);
        setFormData((prev) => ({
            ...prev,
            printer: printer?.value ?? ""
        }));
    };

    const getLoadingMessage = () => (isLoading ? "Processing request..." : "");

    const headerTitle = editDevice ? "Manage Device" : "Add Device";

    return (
        <Modal
            className="location-edit-modal device-manage-modal"
            backdrop="static"
            size="md"
            isOpen={isOpen}
            toggle={handleClose}
            unmountOnClose
        >
            <PreventEnterSubmitForm onSubmit={submitHandler}>
                <ActionModalHeader
                    title={headerTitle}
                    onCancel={handleClose}
                    cancelButtonDisabled={isLoading}
                    actionButton={
                        <Button id="saveChangesButton" variant="submit" size="medium" disabled={isLoading}>
                            Save Changes
                        </Button>
                    }
                />

                <ModalBody className="pt-2 pr-0 pb-3">
                    {isLoading && <Spinner text={getLoadingMessage()} />}

                    {/* EDIT / MANAGE EXISTING DEVICE */}
                    {!isLoading && manageData != null && (
                        <>
                            {/* Hidden fields for submit */}
                            <input
                                type="hidden"
                                id="accountId"
                                value={formData.accountId}
                            />
                            <input
                                type="hidden"
                                id="deviceType"
                                value={formData.deviceType}
                            />

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="name" sm={4} className="col-form-label">
                                    Device Name
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        className="modal-input"
                                        id="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="uDID" sm={4} className="col-form-label">
                                    UDID
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        id="uDID"
                                        readOnly
                                        disabled
                                        value={formData.uDID}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="serialNumber" sm={4} className="col-form-label">
                                    Serial Number
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        id="serialNumber"
                                        readOnly
                                        disabled
                                        value={formData.serialNumber}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="lastUpdated" sm={4} className="col-form-label">
                                    Last Updated
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        id="lastUpdated"
                                        readOnly
                                        disabled
                                        value={formData.lastUpdated}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label
                                    for="DeviceTypeName"
                                    sm={4}
                                    className="col-form-label"
                                >
                                    Device Type
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        id="DeviceTypeName"
                                        readOnly
                                        disabled
                                        value={formData.DeviceTypeName}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label
                                    for="description"
                                    sm={4}
                                    className="col-form-label"
                                >
                                    Description
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        id="description"
                                        className="modal-input"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="location" sm={4} className="col-form-label">
                                    Location
                                </Label>
                                <Col sm={8}>
                                    <Select
                                        id="location"
                                        name="location"
                                        options={locationData}
                                        value={locationSelectedOption}
                                        onChange={handleLocationSelectChange}
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="printer" sm={4} className="col-form-label">
                                    Printer
                                </Label>
                                <Col sm={8}>
                                    <Select
                                        id="printer"
                                        name="printer"
                                        options={printers}
                                        value={printerSelectedOption}
                                        onChange={handlePrinterSelectChange}
                                        isDisabled={manageData.deviceType === 2}
                                    />
                                </Col>
                            </FormGroup>

                            {editDevice && (
                                <>
                                    <div className="mt-3">
                                        <SubscriptionStatus
                                            deviceId={manageData?.accountId}
                                            refreshDevices={closeModalAndRefreshPage}
                                            toastCallback={toastCallback}
                                        />
                                    </div>

                                    {manageData?.subscription === null && (
                                        <div className="d-flex justify-content-end mt-3">
                                            <Button
                                                variant="danger"
                                                size="small"
                                                type="button"
                                                onClick={handleClose}
                                            >
                                                Activate
                                            </Button>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}

                    {/* CREATE NEW DEVICE (manageData == null) */}
                    {!isLoading && manageData == null && (
                        <>
                            <input
                                type="hidden"
                                id="accountId"
                                value={formData.accountId}
                            />

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label
                                    for="name"
                                    sm={4}
                                    className="col-form-label"
                                >
                                    Device Name
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        className="modal-input"
                                        id="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label
                                    for="description"
                                    sm={4}
                                    className="col-form-label"
                                >
                                    Description
                                </Label>
                                <Col sm={8}>
                                    <Input
                                        className="modal-input"
                                        id="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        bsSize="sm"
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="location" sm={4} className="col-form-label">
                                    Location
                                </Label>
                                <Col sm={8}>
                                    <Select
                                        id="location"
                                        name="location"
                                        options={locationData}
                                        value={locationSelectedOption}
                                        onChange={handleLocationSelectChange}
                                    />
                                </Col>
                            </FormGroup>

                            <FormGroup row className="mb-1 align-items-center mr-2">
                                <Label for="printer" sm={4} className="col-form-label">
                                    Printer
                                </Label>
                                <Col sm={8}>
                                    <Select
                                        id="printer"
                                        name="printer"
                                        options={printers}
                                        value={printerSelectedOption}
                                        onChange={handlePrinterSelectChange}
                                    />
                                </Col>
                            </FormGroup>
                        </>
                    )}
                </ModalBody>
            </PreventEnterSubmitForm>
        </Modal>
    );
};

export default ManageDeviceModal;
